<template>
  <default-layout>
    <!--v-content -->
    <v-content>
      <v-container>
        <!-- alert -->
        <snackbar ref="alert" class="mb-5"></snackbar>
        <!--/alert-->

        <!-- header -->
        <header-section header-title="Links" v-model="search" :add-new-item="addLink">
          <template v-slot:addNew>
            <v-layout row wrap justify-space-between align-end>
              <v-btn color="primary" block @click.stop="addLink" :disabled="dialog">Add New</v-btn>
              <v-btn color="info" block @click.stop="openGooglePicker" :disabled="dialog">From Drive</v-btn>
            </v-layout>

            <v-dialog
              v-model="dialog"
              max-width="400px"
              v-if="addLink"
              content-class="dialog-modal"
            >
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-form ref="form" v-model="validForm" lazy-validation>
                      <v-layout wrap>
                        <v-flex xs12>
                          <v-text-field
                            v-model="selectedLink.label"
                            :rules="formValidationRules"
                            label="Label*"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-text-field
                            v-model="selectedLink.description"
                            :rules="formValidationRules"
                            label="Description*"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-text-field
                            v-model="selectedLink.url"
                            :rules="formValidationRules"
                            label="URL*"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-select
                            v-model="selectedLink.category"
                            :items="categories"
                            :item-text="formSelectConfig.text"
                            :item-value="formSelectConfig.value"
                            label="Category*"
                            :rules="formValidationRules"
                            return-object
                            menu-props="offset-y"
                            single-line
                          ></v-select>
                        </v-flex>

                        <v-flex xs12>
                          <v-select
                            v-model="selectedLink.department"
                            :items="departments"
                            :item-text="formSelectConfig.text"
                            :item-value="formSelectConfig.value"
                            label="Department*"
                            :rules="formValidationRules"
                            return-object
                            menu-props="offset-y"
                            single-line
                          ></v-select>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" flat @click="close">Cancel</v-btn>
                  <v-btn color="blue darken-1" flat @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </header-section>
        <!--/header -->

        <!--table -->
        <v-layout row wrap>
          <v-flex xs12>
            <v-data-table :headers="headers" :items="links" :search="search" class="elevation-2">
              <template v-slot:items="props">
                <td>
                  <a :href="props.item.url" target="_blank">
                    {{
                    props.item.label
                    }}
                  </a>
                </td>
                <td>{{ props.item.description }}</td>
                <td>{{ props.item.category.name }}</td>
                <td>{{ props.item.department.name }}</td>
                <td>
                  {{
                  timestampToDate(props.item.created_on)
                  | moment("ddd, MMMM Do YYYY")
                  }}
                </td>
                <td class="justify-center layout px-0">
                  <v-icon
                    small
                    class="mr-2"
                    @click.stop="editLink(props.item)"
                    v-if="isAuthorOrAdmin(props.item)"
                    :disabled="dialog"
                  >edit</v-icon>
                  <v-icon
                    small
                    @click.stop="deleteLink(props.item)"
                    v-if="isAuthorOrAdmin(props.item)"
                    :disabled="dialog"
                  >delete</v-icon>
                </td>
              </template>
              <v-alert
                v-slot:no-results
                :value="true"
                color="error"
                icon="warning"
              >Your search for "{{ search }}" found no results.</v-alert>
            </v-data-table>
          </v-flex>
        </v-layout>
        <!--/table-->
      </v-container>
    </v-content>
    <!--/v-content -->
  </default-layout>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import DefaultLayout from "@/layouts/Default.vue";
import HeaderSection from "@/components/HeaderSection.vue";
import Snackbar from "@/components/Snackbar.vue";
import { timestampToDate } from "@/plugins/firebase";

export default {
  components: {
    DefaultLayout,
    HeaderSection,
    Snackbar
  },
  created() {
    // subcribe to alert mutation
    this.unsubscribeFromStoreAlerts = this.$store.subscribe(
      (mutation, state) => {
        if (mutation.type == "updateAlert") {
          this.customAlert(state.alert.status, state.alert.message);
        }
      }
    );
  },
  destroyed() {
    // unsubscribe from alert mutation
    this.unsubscribeFromStoreAlerts();
    if(this.intervalId){
      window.clearInterval(this.intervalId);
    }
  },
  async mounted() {
    this.showLoading();
    await this.refreshData();
    this.intervalId = window.setInterval(this.refreshData, 60 * 1000);
    this.gapi = await this.$getGapiClient();
    this.initializeGooglePicker();
    this.hideLoading();
  },
  data() {
    return {
      intervalId: undefined,
      gapi: undefined,
      googlePicker: undefined,
      search: "",
      dialog: false,
      validForm: true,
      headers: [
        { text: "Link", align: "left", value: "label" },
        { text: "Description", value: "description", align: "left" },
        { text: "Category", value: "category", align: "left" },
        { text: "Department", value: "department", align: "left" },
        { text: "Created", value: "created_on", align: "left" },
        { text: "Actions", value: "name", sortable: false, align: "center" }
      ],
      selectedLink: {},
      formTitle: "Add New Link",
      inEditMode: false,
      formValidationRules: [v => !!v || "This field is required"],
      formSelectConfig: { text: "name", value: "slug" },
      unsubscribeFromStoreAlerts: undefined
    };
  },
  computed: {
    links() {
      return Object.values(this.$store.state.links);
    },
    departments() {
      return Object.values(this.$store.state.departments);
    },
    categories() {
      return Object.values(this.$store.state.categories);
    }
  },
  methods: {
    ...mapActions([
      "retrieveLinksFromServer",
      "retrieveDepartmentsFromServer",
      "retrieveCategoriesFromServer",
      "createLink",
      "updateLink",
      "destroyLink"
    ]),
    ...mapMutations(["showLoading", "hideLoading"]),
    timestampToDate: timestampToDate,
    async refreshData() {
      await this.retrieveLinksFromServer();

      await this.retrieveDepartmentsFromServer();

      await this.retrieveCategoriesFromServer();
    },
    isFormValid() {
      return this.$refs.form.validate();
    },
    resetValidation() {
      return this.$refs.form.resetValidation();
    },
    addLink() {
      this.close();
      this.openModal();
    },
    openModal() {
      this.dialog = true;
    },
    editLink(link) {
      this.close();
      this.resetValidation();
      this.selectedLink = link;
      this.inEditMode = true;
      this.formTitle = "Edit Link";
      this.openModal();
    },
    async deleteLink(link) {
      this.$root
        .$confirm(
          "Delete Link",
          `Are you sure you wish the '${link.label}' link gone?`,
          {
            color: "red"
          }
        )
        .then(confirm => {
          if (confirm) {
            this.showLoading();
            this.destroyLink(link);
            this.hideLoading();
          }
        });
    },
    customAlert(type, message) {
      this.$refs.alert.open(type, message);
    },
    isAuthorOrAdmin(link) {
      return (
        this.$store.state.currentUser.isAdmin ||
        link.author.email == this.$store.state.currentUser.email
      );
    },
    close() {
      this.dialog = false;
      this.selectedLink = {};
      this.inEditMode = false;
      this.formTitle = "Add New Link";
      this.resetValidation();
    },
    async save() {
      if (this.isFormValid()) {
        const newLink = Object.assign({}, this.selectedLink);
        if (this.inEditMode) {
          this.showLoading();
          await this.updateLink(newLink);
          this.hideLoading();
        } else {
          this.showLoading();
          await this.createLink(newLink);
          this.hideLoading();
        }
        this.close();
      }
    },
    initializeGooglePicker() {
      this.gapi.load("picker", () => {
        const gapiConfig = this.gapi.config.get();
        this.googlePicker = new this.gapi.picker.api.PickerBuilder()
          .enableFeature(this.gapi.picker.api.Feature.SIMPLE_UPLOAD_ENABLED)
          .enableFeature(this.gapi.picker.api.Feature.SUPPORT_TEAM_DRIVES)
          .addView(this.gapi.picker.api.ViewId.DOCS)
          .setOAuthToken(this.$store.state.gapiCredentials.access_token)
          .setDeveloperKey(gapiConfig.client.apiKey)
          .setCallback(this.googlePickerCallback)
          .build();
      });
    },
    openGooglePicker() {
      this.googlePicker.setVisible(true);
    },
    closeGooglePicker() {
      this.googlePicker.setVisible(false);
    },
    googlePickerCallback(data) {
      let url = "nothing";
      if (
        data[this.gapi.picker.api.Response.ACTION] ==
        this.gapi.picker.api.Action.PICKED
      ) {
        const doc = data[this.gapi.picker.api.Response.DOCUMENTS][0];
        url = doc[this.gapi.picker.api.Document.URL];
        this.addLink();
        this.selectedLink.url = url;
      }
    }
  }
};
</script>

<style>
.v-overlay::before {
  background-color: #fff;
  opacity: 1;
}
</style>
